import React, { useState, useEffect } from "react";
import axios from "../../../axios";
import invoiceApi from "../../../api/invoiceApi";
import { toast } from "react-toastify";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Card, CardContent } from "../../../components/ui/card";
import Swal from "sweetalert2";

export default function ZatcaReport() {
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("All");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/ZatcaReport", {
        params: {
          companyId,
          fromDate,
          toDate,
          status,
        },
      });
      setReportData(res.data);
    } catch (error) {
      console.error("Failed to fetch report", error);
      Swal.fire("Error", "فشل تحميل التقرير", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) fetchReport();
  }, [companyId]);

  const handleResend = async (id) => {
    setSendingId(id);
    try {
      // Assuming 'approve' endpoint handles the ZATCA reporting logic
      await invoiceApi.approve(id);
      toast.success("تم إرسال الفاتورة للهيئة بنجاح ✅");
      fetchReport(); // Refresh list
    } catch (error) {
      console.error("Failed to send invoice", error);
      toast.error("فشل إرسال الفاتورة للهيئة ❌");
    } finally {
      setSendingId(null);
    }
  };

  const showDetails = (item) => {
    Swal.fire({
      title: "تفاصيل استجابة الهيئة",
      html: `
        <div style="text-align:left; direction:ltr; max-height:300px; overflow:auto;">
          <p><strong>Status:</strong> ${item.zatcaStatus}</p>
          <p><strong>UUID:</strong> ${item.zatcaUuid || "N/A"}</p>
          <p><strong>Hash:</strong> ${item.zatcaHash || "N/A"}</p>
          <hr/>
          <strong>Warnings:</strong>
          <pre>${item.warnings || "None"}</pre>
          <hr/>
          <strong>Errors:</strong>
          <pre>${item.errors || "None"}</pre>
        </div>
      `,
      width: "600px",
    });
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">تقرير الفوترة الإلكترونية (ZATCA)</h2>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-bold mb-1">من تاريخ</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">إلى تاريخ</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">الحالة</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-40 border rounded px-3 py-2"
              >
                <option value="All">الكل</option>
                <option value="REPORTED">تم الإرسال (Reported)</option>
                <option value="CLEARED">تم الاعتماد (Cleared)</option>
                <option value="FAILED">فشل (Failed)</option>
                <option value="Not Sent">لم يرسل</option>
              </select>
            </div>
            <Button onClick={fetchReport} disabled={loading}>
              {loading ? "جاري البحث..." : "بحث 🔍"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 border"># الفاتورة</th>
              <th className="px-4 py-2 border">التاريخ</th>
              <th className="px-4 py-2 border">العميل</th>
              <th className="px-4 py-2 border">الإجمالي</th>
              <th className="px-4 py-2 border">الحالة</th>
              <th className="px-4 py-2 border">وقت الإرسال</th>
              <th className="px-4 py-2 border">UUID</th>
              <th className="px-4 py-2 border">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 text-center">
                <td className="px-4 py-2 border font-bold">{item.invoiceNumber}</td>
                <td className="px-4 py-2 border">{item.invoiceDate}</td>
                <td className="px-4 py-2 border">{item.customerName}</td>
                <td className="px-4 py-2 border font-bold text-blue-600">
                  {item.totalAmount.toLocaleString()}
                </td>
                <td className="px-4 py-2 border">
                  <span
                    className={`px-2 py-1 rounded text-white text-xs font-bold ${
                      item.zatcaStatus === "REPORTED" || item.zatcaStatus === "CLEARED"
                        ? "bg-green-500"
                        : item.zatcaStatus === "FAILED"
                        ? "bg-red-500"
                        : "bg-gray-400"
                    }`}
                  >
                    {item.zatcaStatus}
                  </span>
                </td>
                <td className="px-4 py-2 border text-sm">
                  {item.reportedAt ? new Date(item.reportedAt).toLocaleString() : "-"}
                </td>
                <td className="px-4 py-2 border text-xs text-gray-500 font-mono">
                  {item.zatcaUuid ? item.zatcaUuid.substring(0, 8) + "..." : "-"}
                </td>
                <td className="px-4 py-2 border">
                  {item.zatcaStatus !== "Not Sent" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => showDetails(item)}
                      className="ml-2"
                    >
                      تفاصيل
                    </Button>
                  )}
                  {(item.zatcaStatus === "Not Sent" || item.zatcaStatus === "FAILED") && (
                     <Button
                       variant="default"
                       size="sm"
                       className="bg-blue-600 hover:bg-blue-700 text-white"
                       onClick={() => handleResend(item.id)}
                       disabled={sendingId === item.id}
                     >
                       {sendingId === item.id ? "جاري الإرسال..." : "ترحيل للهيئة 🚀"}
                     </Button>
                  )}
                </td>
              </tr>
            ))}
            {reportData.length === 0 && !loading && (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  لا توجد بيانات
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}